import { useState } from "react";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { trpc } from "../../lib/trpc";

interface BomViewerProps {
  open: boolean;
  producedKind: "semi" | "finished" | null;
  producedId: string | null;
  producedLabel?: string;
  onClose: () => void;
}

// tRPC dönüş tipi (recipe.explode) — inline tip
interface BomNode {
  itemId: string;
  kind: string;
  id: string;
  code: string;
  name: string;
  neededQty: number;
  uom: string;
  level: number;
  isLeaf: boolean;
  hasRecipe: boolean;
  children: BomNode[];
}

function fmtQty(n: number): string {
  // TR format: binlik nokta, ondalık virgül, max 3 ondalık
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}

function TreeNode({ node, depth }: { node: BomNode; depth: number }) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const kindColor =
    node.kind === "stock" ? "var(--neutral-500)" :
    node.kind === "semi" ? "var(--info)" : "var(--success)";
  const kindLabel = node.kind === "stock" ? "HM" : node.kind === "semi" ? "YM" : "MM";

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "7px 10px",
          paddingLeft: 10 + depth * 20,
          borderBottom: "1px solid var(--neutral-100)",
          fontSize: 13,
        }}
      >
        <button
          type="button"
          onClick={() => hasChildren && setExpanded((v) => !v)}
          aria-label={expanded ? "Daralt" : "Genişlet"}
          style={{
            width: 16, height: 16, border: 0, background: "transparent",
            color: "var(--neutral-500)", cursor: hasChildren ? "pointer" : "default",
            display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            visibility: hasChildren ? "visible" : "hidden",
            transform: expanded ? "rotate(90deg)" : "none", transition: "transform 100ms",
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
        </button>
        <span style={{ fontSize: 9.5, fontWeight: 600, color: kindColor, background: "var(--neutral-100)", padding: "1px 5px", borderRadius: 4, flexShrink: 0 }}>{kindLabel}</span>
        <span className="mono" style={{ fontSize: 12, fontWeight: 500, color: "var(--neutral-900)", flexShrink: 0 }}>{node.code}</span>
        <span style={{ color: "var(--neutral-600)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{node.name}</span>
        <span className="mono" style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: "var(--neutral-900)", flexShrink: 0 }}>
          {fmtQty(node.neededQty)}<span style={{ color: "var(--neutral-400)", fontSize: 10.5, marginLeft: 3, fontWeight: 500 }}>{node.uom}</span>
        </span>
        {node.isLeaf ? (
          <span style={{ fontSize: 9.5, color: "var(--neutral-400)", flexShrink: 0, width: 50, textAlign: "right" }}>yaprak</span>
        ) : (
          <span style={{ fontSize: 9.5, color: "var(--info)", flexShrink: 0, width: 50, textAlign: "right", display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}><Icon name="cornerDownRight" size={10} />{node.children.length} alt</span>
        )}
      </div>
      {hasChildren && expanded ? node.children.map((c) => <TreeNode key={c.itemId} node={c} depth={depth + 1} />) : null}
    </div>
  );
}

export function BomViewerDrawer({ open, producedKind, producedId, producedLabel, onClose }: BomViewerProps) {
  // Serbest metin girişi: kesirli (0,75) ve ondalık değerlere izin verir.
  // Sorguya yalnızca geçerli (> 0) değer gönderilir; "," da nokta olarak kabul edilir.
  const [qtyText, setQtyText] = useState("1000");
  const targetQty = Number(qtyText.replace(/,/g, "."));
  const validQty = Number.isFinite(targetQty) && targetQty > 0;

  const explodeQ = trpc.recipe.explode.useQuery(
    { producedKind: producedKind!, producedId: producedId!, targetQty: validQty ? targetQty : 1 },
    { enabled: open && !!producedKind && !!producedId && validQty }
  );

  const data = explodeQ.data;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title="BOM Patlatma"
      subtitle={producedLabel ?? "Çok seviyeli malzeme listesi"}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <label className="lbl" htmlFor="bom-qty" style={{ margin: 0 }}>Hedef miktar:</label>
        <input
          id="bom-qty"
          className="form-input"
          type="text"
          inputMode="decimal"
          value={qtyText}
          onChange={(e) => setQtyText(e.target.value)}
          placeholder="örn. 0,75"
          aria-invalid={!validQty}
          style={{ width: 140, height: 32 }}
        />
        {data?.producedUom ? (
          <span style={{ fontSize: 13, fontWeight: 500, color: "var(--neutral-700)" }}>{data.producedUom}</span>
        ) : null}
        {!validQty ? (
          <span style={{ fontSize: 12, color: "var(--danger)" }}>0'dan büyük bir miktar girin</span>
        ) : data ? (
          <span className="mono" style={{ fontSize: 12, color: "var(--neutral-500)" }}>{data.producedCode}</span>
        ) : null}
      </div>

      {explodeQ.isLoading ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--neutral-500)" }}>Hesaplanıyor…</div>
      ) : explodeQ.isError ? (
        <div className="form-banner-err">{explodeQ.error?.message}</div>
      ) : !data || data.tree.length === 0 ? (
        <div className="d-card">
          <div className="d-empty">
            <Icon name="cube" size={24} />
            <p className="m" style={{ fontWeight: 500, color: "var(--neutral-500)" }}>Aktif reçete yok</p>
            <p className="m">Bu ürün için BOM patlatmak üzere bir reçeteyi "Yayına Al".</p>
          </div>
        </div>
      ) : (
        <>
          {/* Ağaç */}
          <div className="d-card" style={{ marginBottom: 16 }}>
            <div className="d-card-head">
              <div className="left"><span className="t">Reçete Ağacı</span><span className="s">{data.maxDepth} seviye</span></div>
              <span className="s">İhtiyaç</span>
            </div>
            <div className="d-card-body flush">
              {data.tree.map((n) => <TreeNode key={n.itemId} node={n} depth={0} />)}
            </div>
          </div>

          {/* Yaprak shopping list */}
          <div className="d-card">
            <div className="d-card-head">
              <div className="left"><span className="t">Hammadde İhtiyacı (toplam)</span><span className="s">{data.leaves.length} kalem</span></div>
            </div>
            <div className="d-card-body flush">
              <div className="dt2-wrap">
                <table className="dt2">
                  <thead>
                    <tr>
                      <th>Kod</th>
                      <th>Malzeme</th>
                      <th className="num">Toplam İhtiyaç</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.leaves.map((l) => (
                      <tr key={`${l.kind}:${l.id}`}>
                        <td className="cell-id">{l.code}</td>
                        <td>{l.name}</td>
                        <td className="cell-num">
                          {fmtQty(l.totalQty)}<span className="unit">{l.uom}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
}
